<script setup>
    // Vue
    import { onMounted, ref, inject, getCurrentInstance } from 'vue';
    import { useRouter } from 'vue-router';

    // Component
    import LineFriendComponent from '@/components/modal/LineFriendComponent.vue';
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useHomeStore } from '@/stores/home/homeStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();

    // APIのドメイン部を管理する。
    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // メニュー取得
    const commonStore = useCommonStore();
    const homeStore = useHomeStore();
    const languageStore = useLanguageStore();
    const reserveStore = useReserveStore();

    const mainHeaderComponent = ref();

    const lineFriendModalDispFlg = ref('');
    const lineFriendComponent = ref();

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // Storeを初期化する。
        commonStore.reset();

        // ホーム画面のStore情報を初期化する。
        homeStore.reset();

        // 予約画面のStore情報を初期化する。
        reserveStore.clearAll();

        // メニュー一覧を取得し画面に描画する。
        await getMenu();

        // お知らせ一覧を取得し画面に描画する。

        // お知らせ一覧APIのリクエストボディを生成する。。
        const requestBody = { Lang: languageStore.getLanguageValue };

        // お知らせ一覧APIをコールする。
        const response = await homeStore.getNewsListAction(API_URL, requestBody);
        if (response.success) {
            // ログイン画面のURLにニュースIDがあり、ログインの正当性が確認できた場合のフラグを判定する。
            if (homeStore.KeepNewsDetail.openNewDetailModalInHomeFlag) {
                // ログイン済みで、お知らせも存在していた場合。
                for (let i = 0; i < homeStore.List.length; i++) {
                    const news = homeStore.List[i];
                    if (news.NoticeHistPk === homeStore.KeepNewsDetail.NoticeHistPk && news.NoticeType === homeStore.KeepNewsDetail.NoticeType) {
                        // お知らせ詳細モーダルを表示する。
                        await newsDetailOpen(news.NoticeType, news.NoticeBulletinPk, news.NoticeHistPk, news.NoticeReaded, i);
                    }
                }
            }

            // URLのニュースIDを引き継いだ情報をリセットする。
            homeStore.resetKeepNewsDetail();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    });

    /**
     * メニュー取得処理
     */
    async function getMenu() {
        // メニュー設定取得APIのクエリパラメーターを生成する。
        const queryParam = { Lang: languageStore.getLanguageValue };

        // メニュー設定取得APIをコールする。
        const response = await homeStore.getMenuAction(API_URL, queryParam);

        if (response.success) {
            // ホーム画面のメニューを取得する過程で、新着情報も取得する。
            if (!utils.isNullEmptyArray(homeStore.Menu1)) {
                for (let i = 0; i < homeStore.Menu1.length; i++) {
                    const menu = homeStore.Menu1[i];

                    // メニュー部の新着情報を初期化する。
                    homeStore.Menu1[i]['ArchiveCheck'] = false;

                    // 新着情報のパラメーターが存在するかチェックする。
                    if (!utils.isNullEmpty(menu.NewArrival)) {
                        // 新着情報確認APIのクエリパラメーターを生成する。
                        const queryParam3 = { FuncNm: menu.NewArrival };

                        // 新着情報確認APIをコールする。
                        const response3 = await homeStore.getArchiveCheckAction(API_URL, queryParam3);

                        if (response3.success) {
                            // 各メニューの新着アイコンを制御する。
                            homeStore.Menu1[i]['ArchiveCheck'] = response3.responseBody.Result;
                        } else {
                            messageDialog('error', response3.errorMessage);
                            if (response3.redirect) {
                                location.href = response3.redirectUrl;
                            }
                        }
                    }
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        // 「SNSでのご相談」メニューが存在する場合は、ラインモーダルを表示する動線を設定する。
        const target = document.querySelector('span[id="lineFriendAdd"]');
        if (!utils.isNull(target)) {
            target.parentElement.addEventListener('click', jumpToLineFriendModal);
        }
    }

    /**
     * Javascriptで設定したラインモーダル表示のクリックイベント。
     * Vue自体のイベント処理への動線を設定する。
     */
    async function jumpToLineFriendModal() {
        lineFriendComponentOpen();
    }

    /**
     * ホーム画面のお知らせ欄のニュースをクリックして、
     * ヘッダー部コンポーネントのお知らせモーダルを開くイベント処理。
     * @param noticeType
     * @param noticeBulletinPk
     * @param noticeHistPk
     * @param noticeReaded
     * @param index クリックしたお知らせの座標。何番目のお知らせかを継承して、お知らせモーダル側のページング座標を制御する。
     */
    async function newsDetailOpen(noticeType, noticeBulletinPk, noticeHistPk, noticeReaded, index) {
        // ヘッダー部コンポーネントを経由して、お知らせモーダルを開く。
        mainHeaderComponent.value.openNewsDetailModalFromHomePage(noticeType, noticeBulletinPk, noticeHistPk, noticeReaded, index);
        document.body.classList.add('modal-open'); //スクロール停止
    }

    /**
     * ラインモーダルを開くイベント処理
     */
    async function lineFriendComponentOpen() {
        lineFriendComponent.value.openModal(API_URL, languageStore.getLanguageValue);
        lineFriendModalDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }

    async function lineFriendComponentClose() {
        lineFriendModalDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }

    async function moveToReservePage(mode) {
        switch (mode) {
            case '1':
                // 相談内容を選んで予約
                await getUserStatusReserveFunction(mode);
                break;
            case '2':
                // プログラムを選んで予約
                await getUserStatusReserveFunction(mode);
                break;
            case '3':
                // 割当てられたプログラムを予約
                await getUserStatusReserveFunction(mode);
                break;
            default:
                // 何もしない。
                break;
        }
    }

    /**
     * ユーザー状態確認（予約）APIをコールする。
     * 現在登録されている件数以上を登録できない場合はエラーを返却して、予約画面に進ませない。
     * @param mode
     */
    async function getUserStatusReserveFunction(mode) {
        // ユーザー状態確認（予約）APIのクエリパラメーターを生成する。
        const queryParam = {
            Mode: mode,
        };

        // ユーザー状態確認（予約）APIをコールする。
        const response = await homeStore.getUserStatusReserveAction(API_URL, queryParam);

        if (response.success) {
            switch (mode) {
                case '1':
                    // 相談内容を選んで予約
                    router.push({ name: 'reserveplanningconsultation' });
                    break;
                case '2':
                    // プログラムを選んで予約
                    router.push({ name: 'reserveplanningprogram' });
                    break;
                case '3':
                    // 割当てられたプログラムを予約
                    router.push({ name: 'reserveplanningpersonal' });
                    break;
                default:
                    // 何もしない。
                    break;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }
</script>

<style></style>

<template>
    <section id="main" class="bg">
        <section id="overlay" :style="{ display: lineFriendModalDispFlg }">
            <LineFriendComponent ref="lineFriendComponent" @lineFriendModal:close="lineFriendComponentClose" />
        </section>
        <MainHeader ref="mainHeaderComponent" />
        <div id="home">
            <div id="leftBlock">
                <ul>
                    <template v-for="menu in homeStore.Menu1" :key="menu.index">
                        <li :class="{ emphasis: menu.ArchiveCheck }">
                            <template v-if="menu.Type === 'Button'">
                                <template v-if="menu.Destination !== null && (menu.Destination.indexOf('https://') !== -1 || menu.Destination.indexOf('http://') !== -1)">
                                    <a :href="menu.Destination" v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                    <!--外部へのリンクが設定されている場合-->
                                </template>
                                <template v-else-if="menu.Destination !== null && menu.Destination.indexOf('?cngCat=') !== -1">
                                    <router-link :to="{ name: 'consultationWebList', query: { cngCat: menu.Destination.split('?cngCat=')[1] } }" v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                    <!--Web相談一覧画面に遷移し、相談モーダルを開き、相談種別を自動的に設定する-->
                                </template>
                                <template v-else-if="menu.Caption !== null && menu.Caption.indexOf('lineFriendAdd') !== -1">
                                    <a v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor, cursor: 'pointer' }" />
                                    <!--SNSでのご相談-->
                                </template>
                                <template v-else>
                                    <template v-if="menu.Destination.indexOf('/reserve/planning/consultation') !== -1">
                                        <a v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" @click="moveToReservePage('1')" />
                                        <!--相談内容を選んで予約-->
                                    </template>
                                    <template v-else-if="menu.Destination.indexOf('/reserve/planning/program') !== -1">
                                        <a v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" @click="moveToReservePage('2')" />
                                        <!--プログラムを選んで予約-->
                                    </template>
                                    <template v-else-if="menu.Destination.indexOf('/reserve/planning/personal') !== -1">
                                        <a v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" @click="moveToReservePage('3')" />
                                        <!--割当てられたプログラムを予約-->
                                    </template>
                                    <template v-else>
                                        <router-link :to="{ path: menu.Destination }" v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                        <!--Web相談一覧画面の相談種別自動判別以外-->
                                    </template>
                                </template>
                            </template>
                            <template v-else-if="menu.Type === 'Guidance1' || menu.Type === 'Guidance2' || menu.Type === 'Guidance3' || menu.Type === 'Guidance4' || menu.Type === 'Guidance5'">
                                <p v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                <!--固定プレート-->
                            </template>
                        </li>
                    </template>
                </ul>
            </div>
            <div id="rightBlock">
                <div class="menu">
                    <ul>
                        <template v-for="menu in homeStore.Menu2" :key="menu.index">
                            <li>
                                <template v-if="menu.Destination !== null && menu.Destination.indexOf('/file/download') !== -1">
                                    <!--ファイルダウンロード-->
                                    <router-link :to="{ path: menu.Destination }" class="btnText">{{ menu.Caption }}</router-link>
                                </template>
                                <template v-else>
                                    <!--外部へのリンクが設定されている場合-->
                                    <a :href="menu.Destination" v-html="menu.Caption" class="btnText" target="_blank" />
                                </template>
                            </li>
                        </template>
                    </ul>
                </div>
                <div class="news">
                    <!--お知らせ-->
                    <h2 class="newsTitle">{{ $t('home.parts.blockTitle1') }}</h2>
                    <ul class="newsArea">
                        <template v-for="(news, index) in homeStore.List" :key="news.index">
                            <li :class="{ unread: !news.NoticeReaded }">
                                <button type="button" @click="newsDetailOpen(news.NoticeType, news.NoticeBulletinPk, news.NoticeHistPk, news.NoticeReaded, index)">
                                    <span class="date">{{ news.NoticeDt }}</span>
                                    <span class="title">{{ news.NoticeSubject }}</span>
                                </button>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
            <!--#rightBlock-->
        </div>
    </section>
</template>
